import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const container = document.getElementById("root");
const app = (
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>
);

// public/index.html ships static SEO fallback tags marked data-rh="true". They
// exist only for crawlers that hit the bare SPA shell WITHOUT running JS.
// Once JS runs, React 19 hoists the per-route <SEO> tags into <head>, so the
// fallbacks are removed first to avoid duplicate description/OG tags.
// Prerendered pages never contain them (scripts/prerender.mjs strips them).
document.querySelectorAll("head [data-rh]").forEach((el) => el.remove());

// "ResizeObserver loop completed with undelivered notifications." is a benign
// browser notice (the observer simply deferred work to the next frame), raised
// e.g. when a Radix Select closes in the same frame its dialog unmounts. It is
// not an application error, but the dev overlay and the console treat it as
// one, so it is silenced here — nothing else is filtered.
const RESIZE_OBSERVER_NOTICE = /^ResizeObserver loop/;
// Capture phase: same-target capture listeners run before the bubble-phase ones the
// dev overlay registered earlier, so stopImmediatePropagation() actually reaches it.
window.addEventListener("error", (event) => {
  if (RESIZE_OBSERVER_NOTICE.test(event.message || "")) event.stopImmediatePropagation();
}, { capture: true });

// Per-page tags the prerenderer wrote into <head> for ONE specific route. When
// the markup is hydrated for that same route, React 19 adopts these elements
// as its own (no duplicates). When it is not — a static host served the
// prerendered home page as its fallback for an unknown URL, /admin or an
// estimate replay link — they describe the wrong page, so they are removed
// before React renders the real page's tags (e.g. the 404 page's noindex).
// Nothing else in <head> (GTM, fonts, icons, verification tag, the preview
// noindex guard from js/site-init.js) is touched.
const PRERENDERED_HEAD_TAGS =
  'head > title, head > meta[name="description"], head > link[rel="canonical"], ' +
  'head > meta[property^="og:"], head > meta[name^="twitter:"]';
const dropPrerenderedHeadTags = () => document.querySelectorAll(PRERENDERED_HEAD_TAGS).forEach((el) => el.remove());

// Production pages are prerendered to static HTML (scripts/prerender.mjs) so
// crawlers see full content without running JS. When that markup is present
// AND it was rendered for this exact route, hydrate it into the interactive
// app. If a static host used the prerendered home page as its SPA fallback
// for some other URL (e.g. /admin or a 404), the markup would not match, so
// we discard it and client-render instead of hydrating a mismatched tree.
const normalizePath = (p) => (p.length > 1 ? p.replace(/\/+$/, "") : p);
const prerenderedRoute = document.querySelector('meta[name="x-prerender-route"]')?.content;
const markupMatchesRoute =
  container.hasChildNodes() &&
  prerenderedRoute &&
  normalizePath(prerenderedRoute) === normalizePath(window.location.pathname);

if (markupMatchesRoute) {
  ReactDOM.hydrateRoot(container, app);
} else {
  if (prerenderedRoute) dropPrerenderedHeadTags();
  if (container.hasChildNodes()) container.replaceChildren();
  ReactDOM.createRoot(container).render(app);
}
